import { Component, OnInit, SecurityContext } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ContentItem, ContentsService, ContentType, PageType,
} from '@nimmsta/ng-commons-lib';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

@Component({
  selector: 'app-information',
  templateUrl: './information-item.component.html',
})
export class InformationItemComponent implements OnInit {
  id!: string;

  contentItem?: ContentItem;

  error?: HttpErrorResponse;

  cleanedContent?: SafeHtml;

  protected readonly ContentType = ContentType;

  protected readonly PageType = PageType;

  constructor(private contentService: ContentsService, private route: ActivatedRoute, private sanitizer: DomSanitizer) {
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      this.load();
    });
  }

  private load() {
    this.contentService.getForView(this.id).subscribe({
      next: (contentItem) => {
        this.contentItem = contentItem;
        this.cleanContent(contentItem.content);
        this.cleanAndAppendStyles(contentItem.content);
      },
      error: (error) => {
        this.error = error;
      },
    });
  }

  /**
   * Cleans the content by sanitizing it and removing unwanted tags and attributes among others.
   * <p>
   * Specifically allows iframes. Styles are removed because they are handled separately.
   * @param content The content to clean.
   */
  private cleanContent(content?: string): void {
    if (content) {
      const clean = DOMPurify.sanitize(content, { ADD_TAGS: ['iframe'], FORBID_TAGS: ['style'] });
      this.cleanedContent = this.sanitizer.bypassSecurityTrustHtml(clean);
    }
  }

  /**
   * Cleans the styles from the content and appends them to the document head.
   * <p>
   * We do this separately to wrap the styles in a container with the content ID to avoid conflicts with other styles.
   * @param content The content from which to extract styles.
   */
  private cleanAndAppendStyles(content?: string): void {
    if (!content) {
      return;
    }
    const styles = Array.from(content.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)).map((match) => match[1]);
    if (styles.length === 0) {
      return;
    }
    const sanitizedStyles = this.sanitizer.sanitize(
      SecurityContext.STYLE,
      `#content-container-${this.id} {\n${styles.join('\n')}\n}`,
    );
    if (!sanitizedStyles) {
      return;
    }
    const style = document.createElement('style');
    style.textContent = sanitizedStyles;
    document.head.appendChild(style);
  }
}
